import React, { useEffect, useState } from "react";
import { Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/lang";
import { useContent } from "@/lib/content";

/**
 * Floating "Agendar Consultoria Grátis" CTA — pulsates periodically to grab
 * attention. Vibration intensity + interval controlled via CMS
 * (home_sections.consulting_vibration_intensity 0.5..2, ..._interval_sec).
 */
export default function BookConsultingButton() {
  const { t } = useLang();
  const { site: SITE = {}, home_sections = {} } = useContent();
  const C = t.cta || {};
  const label = C.consultancy || "Agendar Consultoria Grátis";
  const message = C.consultancyMessage || "Olá Places! Quero agendar uma consultoria gratuita para meu empreendimento.";
  const wa = SITE.whatsapp || "5562999999999";
  const href = `https://wa.me/${wa}?text=${encodeURIComponent(message)}`;

  // CMS-controlled vibration
  const intensity = Number(home_sections.consulting_vibration_intensity ?? 1);
  const intervalSec = Math.max(2, Number(home_sections.consulting_vibration_interval_sec ?? 12));

  // Trigger the vibration every N seconds
  const [pulseKey, setPulseKey] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setPulseKey((k) => k + 1), intervalSec * 1000);
    return () => clearInterval(timer);
  }, [intervalSec]);

  const range = 4 * intensity;
  const rotRange = 1.5 * intensity;

  return (
    <motion.a
      key={pulseKey}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="book-consulting-cta"
      aria-label={label}
      animate={{
        x: [0, -range, range, -range * 0.7, range * 0.7, -range * 0.4, range * 0.4, 0],
        rotate: [0, -rotRange, rotRange, -rotRange * 0.6, rotRange * 0.6, 0],
      }}
      transition={{ duration: 0.7, ease: "easeInOut" }}
      className="fixed bottom-6 left-6 z-40 bg-accent-green text-black rounded-full pl-3 pr-5 py-3 flex items-center gap-2 shadow-2xl border border-black/10 hover:bg-accent-yellow hover:text-black hover:scale-105 transition-all group"
      style={{ transformOrigin: "center" }}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black/40 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-black"></span>
      </span>
      <Calendar className="w-4 h-4" />
      <span className="text-sm font-semibold hidden sm:inline uppercase tracking-wide">{label}</span>
    </motion.a>
  );
}
